/**
 * Strips all entity-type-specific branches from a v1 city config object.
 * The removed data is intended to be sourced from EntityTypeConfig (v2) instead.
 * entity_types is removed entirely — it will be supplied from a separate source.
 *
 * @param {object} config - The v1 city config object (not mutated)
 * @returns {object} A new config object with all entity-specific branches removed
 */
export function stripEntityConfig(config) {
  const newConfig = JSON.parse(JSON.stringify(config));

  // Collect entity type names before removing the arrays.
  // Includes both entity_types[*].name and the root entities[] array so that
  // entity types without a full entity_types entry (e.g. moratorium, annual-permit)
  // are also stripped from mixed-key sections.
  const entityNames = new Set([
    ...(newConfig.entity_types ?? []).map(et => et.name),
    ...(Array.isArray(newConfig.entities) ? newConfig.entities : [])
  ]);

  function removeEntityKeys(obj) {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return;
    }
    entityNames.forEach(name => {
      delete obj[name];
    });
  }

  // ── Whole-section removals ──────────────────────────────────────────────

  delete newConfig.entity_types;      // → v2 EntityType
  delete newConfig.entities;          // derived from entity_types
  delete newConfig.attachments;       // → v2 EntityTypeConfig.files
  delete newConfig.schedule;          // → v2 EntityTypeConfig.segment_fields.schedule
  delete newConfig.tray;              // → v2 EntityTypeConfig.display.tray
  delete newConfig['list-item'];      // → v2 EntityTypeConfig.display.legacy
  delete newConfig.export;            // → v2 EntityTypeConfig.display.export

  // ── map.legend.items (entity-driven items) ────────────────────────────
  // → v2 CategoryFieldConfig.map_icon drives legend generation
  // Non-entity items (e.g. layer) are preserved.
  const legendItems = newConfig.map?.legend?.items;
  if (Array.isArray(legendItems)) {
    newConfig.map.legend.items = legendItems.filter(
      item => !Array.isArray(item.entities) || !item.entities.some(name => entityNames.has(name))
    );
  }

  // ── categories.entities ────────────────────────────────────────────────
  // → v2 EntityTypeConfig.fields (|category keys)
  if (newConfig.categories) {
    delete newConfig.categories.entities;
    if (Object.keys(newConfig.categories).length === 0) {
      delete newConfig.categories;
    }
  }

  // ── details.<entity> ──────────────────────────────────────────────────
  // → v2 EntityTypeConfig.fields + display.detail
  // System entries (group, agency, user, batch) are preserved.
  if (newConfig.details) {
    removeEntityKeys(newConfig.details);
  }

  // ── detailTrayConfig.<entity> ──────────────────────────────────────────────────
  // → v2 EntityTypeConfig.fields + display.tray
  // System entries (group, agency, user, batch) are preserved.
  if (newConfig.detailTrayConfig) {
    removeEntityKeys(newConfig.detailTrayConfig);
  }

  // ── logs.tooltips.header.<entity> ────────────────────────────────────────
  // → v2 EntityTypeConfig.display.tooltip
  const tooltipHeader = newConfig.logs?.tooltips?.header;
  if (tooltipHeader) {
    removeEntityKeys(tooltipHeader);
    if (Object.keys(tooltipHeader).length === 0) {
      delete newConfig.logs.tooltips.header;
    }
  }

  // ── logs.tooltips.body.<entity> ────────────────────────────────────────
  // → v2 EntityTypeConfig.display.tooltip
  const tooltipBody = newConfig.logs?.tooltips?.body;
  if (tooltipBody) {
    removeEntityKeys(tooltipBody);
    if (Object.keys(tooltipBody).length === 0) {
      delete newConfig.logs.tooltips.body;
    }
  }

  // ── dashboard.list.paths (entity entries) ─────────────────────────────
  // → derived from all entities in the database in v2
  // Non-entity paths (map, batch, user, agency, etc.) are preserved.
  const listPaths = newConfig.dashboard?.list?.paths;
  if (Array.isArray(listPaths)) {
    newConfig.dashboard.list.paths = listPaths.filter(name => !entityNames.has(name));
  }

  // ── dashboard.list.datatable.columns.<entity> ──────────────────────────
  // → v2 EntityTypeConfig.display.dashboard
  // System columns (group, batch, user, agency, etc.) are preserved.
  const columns = newConfig.dashboard?.list?.datatable?.columns;
  if (columns) {
    removeEntityKeys(columns);
    // columns.overlap.<entity> → v2 EntityTypeConfig.display.overlap_dashboard
    if (columns.overlap) {
      removeEntityKeys(columns.overlap);
      if (Object.keys(columns.overlap).length === 0) {
        delete columns.overlap;
      }
    }
  }

  // ── dashboard.list.datatable.config.<entity> ───────────────────────────
  // → v2 EntityTypeConfig.filters.dashboard (default filter params)
  // System keys (initialFilters, group, batch) are preserved.
  const datatableConfig = newConfig.dashboard?.list?.datatable?.config;
  if (datatableConfig) {
    removeEntityKeys(datatableConfig);
    // config.overlap.<entity> → v2 EntityTypeConfig.filters.overlap_dashboard defaults
    if (datatableConfig.overlap) {
      removeEntityKeys(datatableConfig.overlap);
      if (Object.keys(datatableConfig.overlap).length === 0) {
        delete datatableConfig.overlap;
      }
    }
  }

  // ── dashboard.list['top-bar'].<entity> ────────────────────────────────
  // → can be derived from permission checks instead.
  // System keys (group, batch) are preserved.
  const topBar = newConfig.dashboard?.list?.['top-bar'];
  if (topBar) {
    removeEntityKeys(topBar);
  }

  // ── dashboard.list.menu[*].items (entity-typed items) ─────────────────
  // → derived from entities in the database in v2
  // Non-entity items (batch, group, overlaps with no entity children) are preserved.
  const menuGroups = newConfig.dashboard?.list?.menu;
  if (Array.isArray(menuGroups)) {
    menuGroups.forEach(group => {
      if (!Array.isArray(group.items)) {
        return;
      }
      group.items = group.items
        .map(item => {
          if (!Array.isArray(item.children)) {
            return item;
          }
          const filtered = item.children.filter(child => !entityNames.has(child.type));
          return { ...item, children: filtered };
        })
        .filter(item => !entityNames.has(item.type) && (!Array.isArray(item.children) || item.children.length > 0));
    });
  }

  // ── filters.map.<entity>Filters ────────────────────────────────────────
  // → v2 EntityTypeConfig.filters.map (param mappings derived from MapFilterItem types)
  // Non-entity key mapFilters is preserved.
  if (newConfig.filters?.map && typeof newConfig.filters.map === 'object') {
    entityNames.forEach(entityName => {
      delete newConfig.filters.map[`${entityName}Filters`];
    });
    if (Object.keys(newConfig.filters.map).length === 0) {
      delete newConfig.filters.map;
    }
  }

  // ── filters.entities.<entity> ──────────────────────────────────────────
  // → v2 EntityTypeConfig.filters.map MapFilterItem.default
  if (newConfig.filters?.entities) {
    removeEntityKeys(newConfig.filters.entities);
    if (Object.keys(newConfig.filters.entities).length === 0) {
      delete newConfig.filters.entities;
    }
  }

  // ── filters.filters[] (map panel entity sections) ──────────────────────
  // Entries with name === "entity" → v2 EntityTypeConfig.filters.map
  // Non-entity entries (date, sep, group, layer) are preserved.
  if (Array.isArray(newConfig.filters?.filters)) {
    newConfig.filters.filters = newConfig.filters.filters.filter(
      item => item.name !== 'entity'
    );
  }

  // ── filters.dashboard.<entity> ─────────────────────────────────────────
  // → v2 EntityTypeConfig.filters.dashboard
  // System entries (global, batch, group, agency, cycle, task, etc.) are preserved.
  const dashFilters = newConfig.filters?.dashboard;
  if (dashFilters) {
    removeEntityKeys(dashFilters);
    // filters.dashboard.overlap.<entity> → v2 EntityTypeConfig.filters.overlap_dashboard
    if (dashFilters.overlap) {
      removeEntityKeys(dashFilters.overlap);
      if (Object.keys(dashFilters.overlap).length === 0) {
        delete dashFilters.overlap;
      }
    }
  }

  // ── api.serializer.extra_kwargs.<entity> ───────────────────────────────
  // → v2 EntityTypeConfig.fields (validation error messages)
  const extraKwargs = newConfig.api?.serializer?.extra_kwargs;
  if (extraKwargs) {
    removeEntityKeys(extraKwargs);
  }

  // ── autocomplete.map.<entity> / autocomplete.overlap.<entity> ──────────
  // → v2 EntityTypeConfig.fields[*].use_in_autocomplete
  // autocomplete.common / .activity / .cycle / .task are system entries, preserved.
  if (newConfig.autocomplete?.map) {
    removeEntityKeys(newConfig.autocomplete.map);
    if (Object.keys(newConfig.autocomplete.map).length === 0) {
      delete newConfig.autocomplete.map;
    }
  }
  if (newConfig.autocomplete?.overlap) {
    removeEntityKeys(newConfig.autocomplete.overlap);
    if (Object.keys(newConfig.autocomplete.overlap).length === 0) {
      delete newConfig.autocomplete.overlap;
    }
  }

  // ── batch.<entity>.normalizer (v2-derivable keys) ──────────────────────
  // boolean_fields → v2 fields with type BooleanField
  // category_map   → v2 |category fields with normalizer.batch_mapped_keys
  // key_map        → v2 fields with normalizer.batch_mapped_keys
  // owners_key     → v2 fields.owner.normalizer.batch_mapped_keys
  // contact_fields is NOT in v2 (deprecated built-in) — preserved in v1.
  const BATCH_NORMALIZER_V2_KEYS = ['boolean_fields', 'category_map', 'key_map', 'owners_key'];
  if (newConfig.batch) {
    entityNames.forEach(entityName => {
      const entry = newConfig.batch[entityName];
      if (!entry) {
        return;
      }
      if (entry.normalizer) {
        BATCH_NORMALIZER_V2_KEYS.forEach(key => {
          delete entry.normalizer[key];
        });
        if (Object.keys(entry.normalizer).length === 0) {
          delete entry.normalizer;
        }
      }
      if (Object.keys(entry).length === 0) {
        delete newConfig.batch[entityName];
      }
    });
    if (Object.keys(newConfig.batch).length === 0) {
      delete newConfig.batch;
    }
  }
  return newConfig;
}
